/**
 * Guest session: a short-lived JWT (sessionStorage) plus a remembered-device token
 * (localStorage, ADR-016) that silently re-verifies this phone whenever the stay is active.
 * Every way in (link, code, check-in day + name) stores one, on as many phones as sign in, and
 * the provider resumes it by itself whenever there's no live token (ADR-045), on any screen.
 * Once the stay is over, or when the guest picks "just the house info", the phone is in
 * house-only mode (ADR-041): the house guide, Where's the…? and local picks, nothing personal.
 */
import { ApiClient, ApiError } from '@pmh/shared';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Session } from './types';

const API_BASE = import.meta.env.VITE_API_BASE ?? '';
const K_TOKEN = 'pmh.guest.token';
const K_TOKEN_AT = 'pmh.guest.tokenAt';
const K_BOOKING = 'pmh.guest.booking';
const K_DEVICE = 'pmh.guest.device';
const K_HOUSE_ONLY = 'pmh.guest.houseOnly';

function read(store: Storage, key: string): string | null {
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
}
function write(store: Storage, key: string, value: string | null): void {
  try {
    if (value === null) store.removeItem(key);
    else store.setItem(key, value);
  } catch {
    /* private mode: the session just won't persist */
  }
}

/**
 * When a session token stops working, in ms on this phone's clock, or null if it can't be read.
 * The API Gateway turns an expired token away itself, without CORS headers, so the browser
 * reports a network error, not a 401, and the app would spin forever on the old token (Rook,
 * 2026-10-19). So the app drops a token a minute before it runs out (or when the guest comes back
 * after it has) and signs back in with the remembered device instead. The token's lifetime
 * (`exp` - `iat`) counts from when this phone got it, so a phone whose clock is off neither
 * keeps a dead token nor drops every new one.
 */
export function tokenExpiry(token: string, receivedAt: number | null = null): number | null {
  try {
    const payload = token.split('.')[1] ?? '';
    const { exp, iat } = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as { exp?: unknown; iat?: unknown };
    if (typeof exp !== 'number') return null;
    return receivedAt !== null && typeof iat === 'number' ? receivedAt + (exp - iat) * 1000 : exp * 1000;
  } catch {
    return null;
  }
}

const EXPIRY_MARGIN = 60_000;

function receivedAt(): number | null {
  const at = Number(read(sessionStorage, K_TOKEN_AT));
  return Number.isFinite(at) && at > 0 ? at : null;
}

export function isLive(token: string, at: number | null = receivedAt(), now = Date.now()): boolean {
  const exp = tokenExpiry(token, at);
  return exp === null || exp - EXPIRY_MARGIN > now;
}

function liveToken(): string | null {
  const t = read(sessionStorage, K_TOKEN);
  return t && isLive(t) ? t : null;
}

function hasDevice(bookingId: string | null): boolean {
  return !!bookingId && !!read(localStorage, `${K_DEVICE}.${bookingId}`);
}

interface SessionState {
  bookingId: string | null;
  token: string | null;
  /** No stay on this phone, just the house info (picked, or the stay is over). */
  houseOnly: boolean;
  /** A remembered phone is being signed back in; nothing is decided yet. */
  resuming: boolean;
  api: ApiClient;
  publicApi: ApiClient;
  signIn: (s: Session) => void;
  signOut: () => void;
  resume: (bookingId?: string) => Promise<boolean>;
  showHouseOnly: () => void;
  /** Forget the stay on this phone ("Switch stay"); the lookup form comes next. */
  forget: () => void;
}

const Ctx = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(liveToken);
  const [bookingId, setBookingId] = useState<string | null>(() => read(localStorage, K_BOOKING));
  const [houseOnly, setHouseOnly] = useState(() => read(localStorage, K_HOUSE_ONLY) === '1');

  const signIn = useCallback((s: Session) => {
    write(sessionStorage, K_TOKEN, s.token);
    write(sessionStorage, K_TOKEN_AT, String(Date.now()));
    write(localStorage, K_BOOKING, s.bookingId);
    write(localStorage, `${K_DEVICE}.${s.bookingId}`, s.deviceToken);
    write(localStorage, K_HOUSE_ONLY, null);
    setToken(s.token);
    setBookingId(s.bookingId);
    setHouseOnly(false);
  }, []);

  /** Drop the stay from this phone; `houseOnly` decides what "/" shows next. */
  const leave = useCallback((toHouse: boolean) => {
    const b = read(localStorage, K_BOOKING);
    if (b) write(localStorage, `${K_DEVICE}.${b}`, null);
    write(localStorage, K_BOOKING, null);
    write(sessionStorage, K_TOKEN, null);
    write(localStorage, K_HOUSE_ONLY, toHouse ? '1' : null);
    setToken(null);
    setBookingId(null);
    setHouseOnly(toHouse);
  }, []);
  const showHouseOnly = useCallback(() => leave(true), [leave]);
  const forget = useCallback(() => leave(false), [leave]);

  const signOut = useCallback(() => {
    write(sessionStorage, K_TOKEN, null);
    setToken(null);
  }, []);

  // A token that has run out is dropped, here and a minute before it does (see tokenExpiry).
  useEffect(() => {
    if (!token) {
      if (read(sessionStorage, K_TOKEN)) write(sessionStorage, K_TOKEN, null);
      return;
    }
    const exp = tokenExpiry(token, receivedAt());
    if (exp === null) return;
    const timer = window.setTimeout(signOut, Math.max(0, exp - EXPIRY_MARGIN - Date.now()));
    return () => window.clearTimeout(timer);
  }, [token, signOut]);

  const publicApi = useMemo(() => new ApiClient({ baseUrl: API_BASE }), []);

  const resume = useCallback(
    async (forBooking?: string) => {
      const b = forBooking ?? bookingId;
      if (!b) return false;
      const device = read(localStorage, `${K_DEVICE}.${b}`);
      if (!device) return false;
      try {
        const s = await publicApi.post<Session>('/guest/v1/session/device', { deviceToken: device });
        signIn(s);
        return true;
      } catch (e) {
        if (e instanceof ApiError && e.status === 410) {
          leave(true); // the stay is over: the house info, never "who are you?" again
        } else if (e instanceof ApiError && e.status === 401) {
          write(localStorage, `${K_DEVICE}.${b}`, null);
          if (b === read(localStorage, K_BOOKING)) {
            write(localStorage, K_BOOKING, null);
            setBookingId(null);
          }
        }
        return false;
      }
    },
    [bookingId, publicApi, signIn, leave],
  );

  // No live token but a remembered stay (new tab, reopened app, token ran out): sign back in.
  // `stuckOn` stops a retry loop when the network fails; the lookup form takes over then.
  const [stuckOn, setStuckOn] = useState<string | null>(null);
  const resuming = !token && hasDevice(bookingId) && stuckOn !== bookingId;
  useEffect(() => {
    if (!resuming) return;
    let live = true;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- resume sets state only after its request
    void resume().then((ok) => live && setStuckOn(ok ? null : bookingId));
    return () => {
      live = false;
    };
  }, [resuming, bookingId, resume]);

  const api = useMemo(
    () =>
      new ApiClient({
        baseUrl: API_BASE,
        // A background tab's timer can run late: never send a token that has run out.
        getToken: () => {
          const t = liveToken();
          if (!t && read(sessionStorage, K_TOKEN)) signOut();
          return t;
        },
        onUnauthorized: signOut,
      }),
    [signOut],
  );

  const value = useMemo(
    () => ({ bookingId, token, houseOnly, resuming, api, publicApi, signIn, signOut, resume, showHouseOnly, forget }),
    [bookingId, token, houseOnly, resuming, api, publicApi, signIn, signOut, resume, showHouseOnly, forget],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionState {
  const s = useContext(Ctx);
  if (!s) throw new Error('useSession outside SessionProvider');
  return s;
}
